import L from 'leaflet';
import { track } from '../analytics/events.js';
import './transitStops.css';

// Bus stops and tunnelbana stations on the base map, in both views. The stops come from the map's own tiles
// (OpenFreeMap's poi layer: name, position and OpenStreetMap id), drawn by two symbol layers we add to the
// MapLibre instances that draw symbols (openFreeMap.js: the Positron labels in Fyllda, the Bright / lifted Dark
// base in Linjer, Ingen and Av). The tiles don't carry which lines stop where, so a click looks the stop up in
// public/transit/<cell>.json, built from OpenStreetMap's route relations by scripts/build_transit_lines.py.
//
// Hovering a stop (with a mouse) grows its badge and shows its name; a click or tap opens a small card with its
// lines. A click elsewhere on the map while the card is open only closes it.

export const SUBWAY_LAYER_ID = 'omr_subway_stations';
export const BUS_STOP_LAYER_ID = 'omr_bus_stops';

// In the OpenMapTiles poi layer a tunnelbana station is class "railway", subclass "subway"; a bus stop is class
// "bus", subclass "bus_stop" (a terminal "bus_station"). No trams, commuter rail or airports come with them.
const SUBWAY_FILTER = ['all', ['==', ['get', 'class'], 'railway'], ['==', ['get', 'subclass'], 'subway']];
const BUS_STOP_FILTER = ['all', ['==', ['get', 'class'], 'bus'], ['in', ['get', 'subclass'], ['literal', ['bus_stop', 'bus_station']]]];

// The GL map runs a step behind Leaflet (512px tiles, zoom = Leaflet - 1). Tunnelbana shows at the three
// closest zooms (Leaflet 15–17), bus stops, which are many times more, at the two closest (Leaflet 16–17).
const SUBWAY_MIN_ZOOM = 14;
const BUS_STOP_MIN_ZOOM = 15;

// Badge sizes: the icons are drawn on a 24 unit square, shown at these icon-sizes.
const BADGE_UNITS = 24;
const ICON_SIZE = { subway: 0.8, bus: 0.66 };
const HOVER_SCALE = 1.4;

// The tunnelbana marker is the Stockholm symbol everyone knows: a round white badge with a blue border and a
// blue "T". The bus stop is a slate disc with a white bus, quieter since there are so many. Both have a thin
// white outer halo that keeps them legible over the darker area colours.
const TUNNELBANA_BLUE = '#0a6cb0';
const BUS_SLATE = '#3f5560';
const ICON_IDS = { subway: 'omr_tunnelbana', bus: 'omr_bus_stop' };

const drawBadge = (ctx, kind) => {
    const c = BADGE_UNITS / 2;
    const disc = (radius, fill) => {
        ctx.beginPath();
        ctx.arc(c, c, radius, 0, Math.PI * 2);
        ctx.fillStyle = fill;
        ctx.fill();
    };
    disc(c - 0.5, '#ffffff');
    if (kind === 'subway') {
        disc(c - 1.8, TUNNELBANA_BLUE);
        disc(c - 4.3, '#ffffff');
        ctx.fillStyle = TUNNELBANA_BLUE;
        ctx.font = `700 ${Math.round(BADGE_UNITS * 0.6)}px -apple-system, "Helvetica Neue", Arial, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('T', c, c + (BADGE_UNITS * 0.06));
        return;
    }
    disc(c - 1.6, BUS_SLATE);
    // A bus from the front: body, windscreen, lights and wheels.
    const rounded = (x, y, w, h, r) => {
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.arcTo(x + w, y, x + w, y + h, r);
        ctx.arcTo(x + w, y + h, x, y + h, r);
        ctx.arcTo(x, y + h, x, y, r);
        ctx.arcTo(x, y, x + w, y, r);
        ctx.closePath();
        ctx.fill();
    };
    ctx.fillStyle = '#ffffff';
    rounded(7, 5.6, 10, 11, 2);
    rounded(7.6, 16, 2.4, 2.4, 0.8);
    rounded(14, 16, 2.4, 2.4, 0.8);
    ctx.fillStyle = BUS_SLATE;
    rounded(8.3, 7.4, 7.4, 4.4, 1);
    ctx.beginPath();
    ctx.arc(9.4, 14.1, 0.85, 0, Math.PI * 2);
    ctx.arc(14.6, 14.1, 0.85, 0, Math.PI * 2);
    ctx.fill();
};

const badgeCanvas = (kind, ratio) => {
    const px = Math.round(BADGE_UNITS * ratio);
    const canvas = document.createElement('canvas');
    canvas.width = px;
    canvas.height = px;
    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);
    drawBadge(ctx, kind);
    return canvas;
};

// The same badge as an image for the hover and the card, drawn sharp for the largest it is shown at.
const badgeUrls = {};
const badgeUrl = (kind) => {
    if (!badgeUrls[kind]) {
        try {
            badgeUrls[kind] = badgeCanvas(kind, Math.ceil(Math.max(2, window.devicePixelRatio || 1) * HOVER_SCALE)).toDataURL();
        } catch (error) {
            badgeUrls[kind] = '';
        }
    }
    return badgeUrls[kind];
};

const ensureBadge = (glMap, kind) => {
    if (glMap.hasImage?.(ICON_IDS[kind])) return;
    try {
        const canvas = badgeCanvas(kind, 2);
        const image = canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height);
        glMap.addImage(ICON_IDS[kind], image, { pixelRatio: 2 });
    } catch (error) {
        // Already added by a concurrent load, or no canvas: the layer falls back to no icon.
    }
};

// Both layers on a MapLibre instance that draws symbols. Tunnelbana is only PLACED here, invisibly (opacity 0):
// the controller below draws each placed station, badge and name, in a pane above the areas, the dimming and the
// homes, so it's never under a line or a shade. Placed with overlap allowed, so a station always shows with its
// name, and everything placed after it (street names, bus stops) gives way to it. Below the place names in the
// layer order. Bus stops have no names on the map (the hover and the card show them) and never push a street name
// off the map (icon-ignore-placement).
export const addTransitLayers = (glMap) => {
    if (!glMap.getSource('openmaptiles')) return;
    const beforePlace = glMap.getStyle().layers.find((layer) => /^(label_|place)/.test(layer.id));
    if (!glMap.getLayer(SUBWAY_LAYER_ID)) {
        ensureBadge(glMap, 'subway');
        glMap.addLayer({
            id: SUBWAY_LAYER_ID,
            type: 'symbol',
            source: 'openmaptiles',
            'source-layer': 'poi',
            minzoom: SUBWAY_MIN_ZOOM,
            filter: SUBWAY_FILTER,
            layout: {
                'icon-image': ICON_IDS.subway,
                'icon-size': ICON_SIZE.subway,
                'icon-allow-overlap': true,
                'text-allow-overlap': true,
                'text-field': ['coalesce', ['get', 'name:latin'], ['get', 'name']],
                'text-font': ['Noto Sans Regular'],
                'text-size': 11.5,
                'text-anchor': 'left',
                'text-offset': [0.95, 0],
                'text-max-width': 8,
            },
            paint: { 'icon-opacity': 0, 'text-opacity': 0 },
        }, beforePlace ? beforePlace.id : undefined);
    }
    if (!glMap.getLayer(BUS_STOP_LAYER_ID)) {
        ensureBadge(glMap, 'bus');
        glMap.addLayer({
            id: BUS_STOP_LAYER_ID,
            type: 'symbol',
            source: 'openmaptiles',
            'source-layer': 'poi',
            minzoom: BUS_STOP_MIN_ZOOM,
            filter: BUS_STOP_FILTER,
            layout: {
                'icon-image': ICON_IDS.bus,
                'icon-size': ICON_SIZE.bus,
                'icon-allow-overlap': false,
                'icon-ignore-placement': true,
                'icon-padding': 1,
            },
        }, SUBWAY_LAYER_ID);
    }
};

// ---- Which lines stop where (public/transit, see scripts/build_transit_lines.py) ----

const CELL_LAT = 0.25;
const CELL_LNG = 0.5;
const cellName = (lat, lng) => `${Math.floor(lat / CELL_LAT)}_${Math.floor(lng / CELL_LNG)}`;
const EMPTY_CELL = { lines: [], stops: {} };
const cells = new Map();
const loadCell = (name) => {
    if (!cells.has(name)) {
        const request = fetch(`${process.env.PUBLIC_URL || ''}/transit/${name}.json`)
            .then((response) => (response.ok ? response.text() : ''))
            .then((text) => {
                // No file for a cell without lines: the host answers with the app's HTML.
                try {
                    const data = JSON.parse(text);
                    return data && data.stops ? data : EMPTY_CELL;
                } catch (error) {
                    return EMPTY_CELL;
                }
            })
            .catch((error) => {
                cells.delete(name);
                throw error;
            });
        cells.set(name, request);
    }
    return cells.get(name);
};

// The tiles' feature id is the OpenStreetMap id × 10 + 1 for a node (2 a way, 3 a relation).
const osmNodeId = (featureId) => {
    const id = Number(featureId);
    return Number.isFinite(id) && id % 10 === 1 ? String(Math.floor(id / 10)) : null;
};

// { lines: [{ label, mode, colour, from, to }], dirs: [{ line, next, to, bearing }] } for the stop, both empty
// when it has none listed. dirs is where each line goes from here: its next stop, its last stop and the compass
// bearing to the next one (null when unknown), where the data has the line's stop order. Rejects on a network
// error.
export const fetchStopLines = async ({ id, latlng }) => {
    const nodeId = osmNodeId(id);
    if (!nodeId) return { lines: [], dirs: [] };
    const cell = await loadCell(cellName(latlng.lat, latlng.lng));
    const lineAt = (index) => {
        const [label, mode, colour, from, to] = cell.lines[index] || [];
        return { label: String(label || ''), mode, colour, from, to };
    };
    const names = cell.names || [];
    const lines = (cell.stops[nodeId] || []).map(lineAt).filter((line) => line.label);
    const dirs = ((cell.dirs || {})[nodeId] || []).map(([index, next, to, bearing]) => ({
        line: lineAt(index),
        next: names[next] || '',
        to: names[to] || '',
        bearing: Number.isFinite(bearing) && bearing >= 0 ? bearing : null,
    })).filter((dir) => dir.line.label && dir.next);
    return { lines, dirs };
};

// ---- The card ----

const MODE_ORDER = ['subway', 'train', 'light_rail', 'tram', 'bus', 'trolleybus', 'ferry', 'monorail'];
const MODE_LABELS = {
    subway: ['Tunnelbana', 'Metro'],
    train: ['Tåg', 'Train'],
    light_rail: ['Spårväg', 'Light rail'],
    tram: ['Spårvagn', 'Tram'],
    bus: ['Buss', 'Bus'],
    trolleybus: ['Trådbuss', 'Trolleybus'],
    ferry: ['Båt', 'Ferry'],
    monorail: ['Monorail', 'Monorail'],
};
const KIND_LABELS = { subway: ['Tunnelbana', 'Metro station'], bus: ['Busshållplats', 'Bus stop'] };
// With this many lines or fewer each gets a row with where it runs; more are shown as numbers only.
const MAX_ROWS = 6;

const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));

// Dark text on a light line colour (Göteborg's white and yellow trams), white on the rest.
const isLightColour = (hex) => {
    const match = /^#([0-9a-f]{6})$/i.exec(hex || '');
    if (!match) return false;
    const [r, g, b] = [0, 2, 4].map((i) => parseInt(match[1].slice(i, i + 2), 16) / 255)
        .map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
    return (0.2126 * r) + (0.7152 * g) + (0.0722 * b) > 0.45;
};

const linePill = (line, title = '') => {
    const classes = ['transit-line'];
    let style = '';
    if (line.colour) {
        classes.push(isLightColour(line.colour) ? 'is-light' : 'is-coloured');
        style = ` style="--line-colour:${line.colour}"`;
    }
    if (line.label.length > 4) classes.push('is-long');
    const tip = title ? ` title="${escapeHtml(title)}"` : '';
    return `<span class="${classes.join(' ')}"${style}${tip}>${escapeHtml(line.label)}</span>`;
};

const byLabel = (a, b) => {
    const numeric = (label) => /^\d/.test(label);
    if (numeric(a.label) !== numeric(b.label)) return numeric(a.label) ? -1 : 1;
    return a.label.localeCompare(b.label, 'sv', { numeric: true });
};

const linesHtml = (lines, kind, swedish) => {
    const groups = MODE_ORDER
        .map((mode) => ({ mode, lines: lines.filter((line) => line.mode === mode).sort(byLabel) }))
        .filter((group) => group.lines.length);
    const rows = lines.length <= MAX_ROWS;
    // The heading already says what the stop is: its own mode needs no label when it's the only one.
    const showLabels = groups.length > 1 || groups[0].mode !== kind;
    return groups.map((group) => {
        const label = showLabels
            ? `<div class="transit-card-mode">${escapeHtml(MODE_LABELS[group.mode][swedish ? 0 : 1])}</div>`
            : '';
        if (!rows) {
            return `${label}<div class="transit-card-pills">${group.lines.map((line) => linePill(line)).join('')}</div>`;
        }
        const items = group.lines.map((line) => {
            const ends = line.from && line.to ? `${line.from} – ${line.to}` : '';
            return `<li>${linePill(line)}<span class="transit-card-ends" title="${escapeHtml(ends)}">${escapeHtml(ends)}</span></li>`;
        });
        return `${label}<ul class="transit-card-rows">${items.join('')}</ul>`;
    }).join('');
};

// ---- Directions (design B "Riktningar"): the lines grouped by where they go next ----

const ARROWS = ['↑', '↗', '→', '↘', '↓', '↙', '←', '↖'];
const arrowFor = (bearing) => (bearing == null ? '→' : ARROWS[Math.round(bearing / 45) % 8]);
// More directions than this (a bus hub) are merged by compass sector, so the card stays short.
const MAX_DIRECTIONS = 4;
// A direction lists its lines as rows with their last stop up to this many, as numbers beyond.
const MAX_DIRECTION_ROWS = 4;

const meanBearing = (bearings) => {
    if (!bearings.length) return null;
    const rad = (deg) => (deg * Math.PI) / 180;
    const x = bearings.reduce((sum, b) => sum + Math.sin(rad(b)), 0);
    const y = bearings.reduce((sum, b) => sum + Math.cos(rad(b)), 0);
    return ((Math.atan2(x, y) * 180) / Math.PI + 360) % 360;
};

// [{ next, bearing, items: [{ line, to }] }], one per next stop, or per compass sector when there are many.
const groupDirections = (dirs) => {
    const byNext = new Map();
    dirs.forEach((dir) => {
        let group = byNext.get(dir.next);
        if (!group) {
            group = { next: dir.next, bearings: [], items: new Map() };
            byNext.set(dir.next, group);
        }
        if (dir.bearing != null) group.bearings.push(dir.bearing);
        const key = `${dir.line.mode}|${dir.line.label}|${dir.to}`;
        if (!group.items.has(key)) group.items.set(key, { line: dir.line, to: dir.to });
    });
    let groups = [...byNext.values()].map((group) => ({ ...group, bearing: meanBearing(group.bearings) }));
    if (groups.length > MAX_DIRECTIONS) {
        const bySector = new Map();
        groups.forEach((group) => {
            const sector = group.bearing == null ? 'none' : Math.round(group.bearing / 45) % 8;
            const merged = bySector.get(sector);
            if (!merged) {
                bySector.set(sector, { ...group, items: new Map(group.items), size: group.items.size });
                return;
            }
            // Named after the stop most of its lines go to next.
            if (group.items.size > merged.size) {
                merged.next = group.next;
                merged.size = group.items.size;
            }
            merged.bearings = merged.bearings.concat(group.bearings);
            merged.bearing = meanBearing(merged.bearings);
            group.items.forEach((item, key) => { if (!merged.items.has(key)) merged.items.set(key, item); });
        });
        groups = [...bySector.values()];
    }
    return groups
        .map((group) => ({ ...group, items: [...group.items.values()].sort((a, b) => byLabel(a.line, b.line)) }))
        .sort((a, b) => (a.bearing ?? 999) - (b.bearing ?? 999));
};

// "Mot Hässelby strand" when its lines all end at one stop, else "Mot <the next stop>".
const directionLabel = (group, swedish) => {
    const ends = new Set(group.items.map((item) => item.to));
    return `${swedish ? 'Mot' : 'To'} ${ends.size === 1 ? [...ends][0] : group.next}`;
};

// compact: numbers only (a stop with more than two directions), each number's last stop in its tooltip.
const directionBody = (group, compact = false) => {
    const ends = new Set(group.items.map((item) => item.to));
    if (compact || ends.size === 1 || group.items.length > MAX_DIRECTION_ROWS) {
        const pills = group.items.map((item) => linePill(item.line, `→ ${item.to}`));
        return `<div class="transit-card-pills">${pills.join('')}</div>`;
    }
    const rows = group.items.map((item) => (
        `<li>${linePill(item.line)}<span class="transit-card-ends" title="${escapeHtml(item.to)}">${escapeHtml(item.to)}</span></li>`
    ));
    return `<ul class="transit-card-rows">${rows.join('')}</ul>`;
};

const directionsHtml = (stop, lines, dirs, swedish) => {
    const groups = groupDirections(dirs);
    if (!groups.length) return '';
    // Lines the data has no stop order for still show, as numbers under the directions.
    const covered = new Set(dirs.map((dir) => `${dir.line.mode}|${dir.line.label}`));
    const rest = lines.filter((line) => !covered.has(`${line.mode}|${line.label}`)).sort(byLabel);
    const restHtml = rest.length
        ? `<div class="transit-card-mode">${swedish ? 'Fler linjer' : 'More lines'}</div><div class="transit-card-pills">${rest.map((line) => linePill(line)).join('')}</div>`
        : '';
    if (groups.length === 2) {
        // A line through the stop: the stop before and after it on a strip, then one column per direction,
        // the one heading west on the left.
        const east = (group) => (group.bearing == null ? 0 : Math.sin((group.bearing * Math.PI) / 180));
        const [left, right] = east(groups[0]) <= east(groups[1]) ? groups : [groups[1], groups[0]];
        const colours = left.items.concat(right.items).map((item) => item.line.colour || '');
        const stripColour = colours.every((colour) => colour && colour === colours[0]) ? colours[0] : '';
        const strip = `
            <div class="transit-strip"${stripColour ? ` style="--strip:${stripColour}"` : ''} aria-hidden="true">
                <span class="transit-strip-line"></span>
                <span class="transit-strip-node is-left"></span>
                <span class="transit-strip-node is-here"></span>
                <span class="transit-strip-node is-right"></span>
                <span class="transit-strip-name is-left">${escapeHtml(left.next)}</span>
                <span class="transit-strip-name is-here">${escapeHtml(stop.name)}</span>
                <span class="transit-strip-name is-right">${escapeHtml(right.next)}</span>
            </div>`;
        const column = (group, side) => `
            <div class="transit-dir is-${side}">
                <div class="transit-dir-head" title="${escapeHtml(directionLabel(group, swedish))}">${side === 'left'
                    ? `← ${escapeHtml(directionLabel(group, swedish))}`
                    : `${escapeHtml(directionLabel(group, swedish))} →`}</div>
                ${directionBody(group)}
            </div>`;
        return `${strip}<div class="transit-dirs is-two">${column(left, 'left')}${column(right, 'right')}</div>${restHtml}`;
    }
    const blocks = groups.map((group) => `
        <div class="transit-dir">
            <div class="transit-dir-head" title="${escapeHtml(directionLabel(group, swedish))}"><span class="transit-dir-arrow">${arrowFor(group.bearing)}</span>${escapeHtml(directionLabel(group, swedish))}</div>
            ${directionBody(group, groups.length > 2)}
        </div>`);
    return `<div class="transit-dirs">${blocks.join('')}</div>${restHtml}`;
};

const cardHtml = (stop, state, swedish) => {
    const kind = KIND_LABELS[stop.kind][swedish ? 0 : 1];
    let body;
    if (state.status === 'loading') {
        body = '<div class="transit-card-skeleton"><span></span><span></span><span></span></div>';
    } else if (state.status === 'error') {
        body = `<p class="transit-card-note">${swedish ? 'Kunde inte hämta linjerna just nu.' : "Couldn't load the lines right now."}</p>`;
    } else if (!state.lines.length) {
        body = `<p class="transit-card-note">${swedish ? 'Inga linjer hittades för hållplatsen.' : 'No lines found for this stop.'}</p>`;
    } else {
        // Where the lines go (directions), or just the lines when the data has no stop order for them.
        body = directionsHtml(stop, state.lines, state.dirs || [], swedish) || linesHtml(state.lines, stop.kind, swedish);
    }
    return `
        <div class="transit-card is-${stop.kind}">
            <div class="transit-card-head">
                <img class="transit-card-badge" src="${badgeUrl(stop.kind)}" alt="" />
                <div class="transit-card-title">
                    <div class="transit-card-name">${escapeHtml(stop.name || kind)}</div>
                    <div class="transit-card-kind">${escapeHtml(kind)}</div>
                </div>
            </div>
            <div class="transit-card-body">${body}</div>
        </div>`;
};

// ---- Hover and click, one controller per Leaflet map ----

// What sits over the map and handles its own clicks: markers, popups, controls.
const OVERLAY_SELECTOR = '.leaflet-marker-icon, .leaflet-popup, .leaflet-control, .leaflet-tooltip';
const HIT_RADIUS = { mouse: 3, touch: 12 };
const isTouchEvent = (event) => event.pointerType === 'touch' || event.pointerType === 'pen';

const createController = (map) => {
    const glMaps = new Set();
    const container = map.getContainer();
    const pane = map.getPane('transitStopPane') || map.createPane('transitStopPane');
    // Above the listing markers (600), below popups (700): the hovered stop isn't hidden under a home.
    pane.style.zIndex = '650';
    pane.style.pointerEvents = 'none';
    let swedish = true;
    let moving = false;
    let hover = null;        // { stop, el }
    let card = null;         // { stop, popup, state }
    let pendingMove = null;
    let moveFrame = null;
    let lastPointer = 'mouse';

    // Tunnelbana stations, drawn above the areas, the dimming and the homes (z 640; the hover pane is 650, popups
    // 700) where the GL layer placed them. Refreshed when a GL map has settled ('idle'); the markers ride the
    // map pane meanwhile, and Leaflet moves them through a zoom.
    const topPane = map.getPane('transitStationPane') || map.createPane('transitStationPane');
    topPane.style.zIndex = '640';
    topPane.style.pointerEvents = 'none';
    const stations = new Map();  // key -> L.marker
    let stationFrame = null;
    const syncStations = () => {
        stationFrame = null;
        const seen = new Set();
        if (map.getZoom() >= SUBWAY_MIN_ZOOM + 1) {
            glMaps.forEach((glMap) => {
                if (!glMap.getLayer?.(SUBWAY_LAYER_ID)) return;
                let features = [];
                try {
                    features = glMap.queryRenderedFeatures({ layers: [SUBWAY_LAYER_ID] });
                } catch (error) {
                    return;
                }
                features.forEach((feature) => {
                    if (feature.geometry?.type !== 'Point') return;
                    const [lng, lat] = feature.geometry.coordinates;
                    const name = String(feature.properties?.['name:latin'] || feature.properties?.name || '');
                    const key = String(feature.id ?? `${name}@${lat},${lng}`);
                    if (seen.has(key)) return;
                    seen.add(key);
                    if (stations.has(key)) return;
                    const icon = L.divIcon({
                        className: 'transit-station',
                        iconSize: null,
                        html: `<img class="transit-station-badge" src="${badgeUrl('subway')}" alt="" />`
                            + (name ? `<span class="transit-station-name">${escapeHtml(name)}</span>` : ''),
                    });
                    const marker = L.marker([lat, lng], { icon, pane: 'transitStationPane', interactive: false, keyboard: false });
                    marker.addTo(map);
                    marker.getElement()?.style.setProperty('--badge', `${badgeSize('subway')}px`);
                    stations.set(key, marker);
                });
            });
        }
        stations.forEach((marker, key) => {
            if (seen.has(key)) return;
            marker.remove();
            stations.delete(key);
        });
    };
    const queueStations = () => {
        if (!stationFrame) stationFrame = requestAnimationFrame(syncStations);
    };
    const clearStations = () => {
        if (stationFrame) cancelAnimationFrame(stationFrame);
        stationFrame = null;
        stations.forEach((marker) => marker.remove());
        stations.clear();
    };

    // The stop under a container point, or null. Any feature the box touches counts (a station's name too);
    // of several, the nearest.
    const stopAt = (point, radius) => {
        // Below Leaflet 15 neither layer draws anything.
        if (moving || map.getZoom() < SUBWAY_MIN_ZOOM + 1) return null;
        const latlng = map.containerPointToLatLng(point);
        let best = null;
        glMaps.forEach((glMap) => {
            const layers = [SUBWAY_LAYER_ID, BUS_STOP_LAYER_ID].filter((id) => glMap.getLayer?.(id));
            if (!layers.length) return;
            let features;
            try {
                const p = glMap.project([latlng.lng, latlng.lat]);
                features = glMap.queryRenderedFeatures([[p.x - radius, p.y - radius], [p.x + radius, p.y + radius]], { layers });
                features.forEach((feature) => {
                    if (feature.geometry?.type !== 'Point') return;
                    const [lng, lat] = feature.geometry.coordinates;
                    const q = glMap.project([lng, lat]);
                    const distance = Math.hypot(q.x - p.x, q.y - p.y);
                    if (best && distance >= best.distance) return;
                    const kind = feature.layer.id === SUBWAY_LAYER_ID ? 'subway' : 'bus';
                    const name = String(feature.properties?.['name:latin'] || feature.properties?.name || '');
                    best = {
                        distance,
                        stop: { kind, id: feature.id, name, latlng: L.latLng(lat, lng), key: `${kind}:${feature.id ?? `${name}@${lat},${lng}`}` },
                    };
                });
            } catch (error) {
                // The style is reloading: no stop this time.
            }
        });
        return best ? best.stop : null;
    };
    const eventPoint = (event) => {
        const rect = container.getBoundingClientRect();
        return L.point(event.clientX - rect.left, event.clientY - rect.top);
    };

    const badgeSize = (kind) => BADGE_UNITS * ICON_SIZE[kind];

    const clearHover = () => {
        if (!hover) return;
        const { el } = hover;
        hover = null;
        container.classList.remove('is-transit-hover');
        el.classList.remove('is-on');
        window.setTimeout(() => el.remove(), 160);
    };
    const showHover = (stop) => {
        if (hover && hover.stop.key === stop.key) return;
        clearHover();
        // Its card already shows its name.
        if (card && card.stop.key === stop.key) return;
        const el = L.DomUtil.create('div', `transit-hover is-${stop.kind}`, pane);
        el.style.setProperty('--badge', `${badgeSize(stop.kind)}px`);
        el.style.setProperty('--grow', String(HOVER_SCALE));
        el.innerHTML = `<img class="transit-hover-badge" src="${badgeUrl(stop.kind)}" alt="" />`
            + (stop.name ? `<span class="transit-hover-name">${escapeHtml(stop.name)}</span>` : '');
        L.DomUtil.setPosition(el, map.latLngToLayerPoint(stop.latlng));
        hover = { stop, el };
        container.classList.add('is-transit-hover');
        // On the next frame, so it grows from the map's size.
        requestAnimationFrame(() => { if (hover && hover.el === el) el.classList.add('is-on'); });
    };

    const onPointerMove = (event) => {
        lastPointer = event.pointerType || 'mouse';
        if (event.pointerType && event.pointerType !== 'mouse') return;
        if (moving || event.buttons || event.target.closest?.(OVERLAY_SELECTOR)) {
            clearHover();
            return;
        }
        pendingMove = eventPoint(event);
        if (moveFrame) return;
        moveFrame = requestAnimationFrame(() => {
            moveFrame = null;
            if (!pendingMove) return;
            const stop = stopAt(pendingMove, HIT_RADIUS.mouse);
            pendingMove = null;
            if (stop) showHover(stop); else clearHover();
        });
    };
    const onPointerLeave = () => {
        pendingMove = null;
        clearHover();
    };
    const onPointerDown = (event) => { lastPointer = event.pointerType || 'mouse'; };

    const renderCard = () => {
        if (!card) return;
        const html = cardHtml(card.stop, card.state, swedish);
        // Two directions side by side need the room (Leaflet sizes the card to its content within these).
        card.popup.options.minWidth = html.includes('transit-dirs is-two') ? 340 : 250;
        card.popup.setContent(html);
    };
    const closeCard = () => {
        if (!card) return;
        const { popup } = card;
        card = null;
        map.closePopup(popup);
    };
    const openCard = (stop) => {
        if (card && card.stop.key === stop.key) return;
        closeCard();
        clearHover();
        const popup = L.popup({
            className: 'transit-stop-popup',
            autoClose: false,
            closeOnClick: false,
            closeButton: true,
            maxWidth: 340,
            minWidth: 250,
            // The tip points at the top of the badge.
            offset: L.point(0, 5 - (badgeSize(stop.kind) / 2)),
            autoPanPaddingTopLeft: L.point(16, 80),
            autoPanPaddingBottomRight: L.point(16, 24),
        }).setLatLng(stop.latlng);
        const current = { stop, popup, state: { status: 'loading', lines: [] } };
        card = current;
        popup.on('remove', () => { if (card === current) card = null; });
        renderCard();
        popup.openOn(map);
        fetchStopLines(stop).then(({ lines, dirs }) => {
            current.state = { status: 'done', lines, dirs };
            track('map.transit_stop', { kind: stop.kind, lines: lines.length, input: lastPointer === 'touch' ? 'touch' : 'mouse' });
        }).catch(() => {
            current.state = { status: 'error', lines: [] };
        }).then(() => {
            if (card === current) renderCard();
        });
    };

    // Capture phase, so a click on a stop never reaches the areas under it (no select, no sticky card).
    const onClick = (event) => {
        if (event.target.closest?.('.transit-stop-popup')) return;
        if (event.target.closest?.(OVERLAY_SELECTOR)) {
            closeCard();
            return;
        }
        if (map.dragging?.moved?.()) return;
        const radius = isTouchEvent(event) || lastPointer !== 'mouse' ? HIT_RADIUS.touch : HIT_RADIUS.mouse + 2;
        const stop = stopAt(eventPoint(event), radius);
        if (stop) {
            event.stopPropagation();
            openCard(stop);
        } else if (card) {
            // A click elsewhere on the map only closes the card, like the listing mini card.
            event.stopPropagation();
            closeCard();
        }
    };
    const onDoubleClick = (event) => {
        if (event.target.closest?.(OVERLAY_SELECTOR)) return;
        const stop = stopAt(eventPoint(event), lastPointer !== 'mouse' ? HIT_RADIUS.touch : HIT_RADIUS.mouse + 2);
        if (stop || card) event.stopPropagation();
    };
    const onKeyDown = (event) => { if (event.key === 'Escape') closeCard(); };
    const onMoveStart = () => {
        moving = true;
        clearHover();
    };
    const onMoveEnd = () => {
        moving = false;
        // Below the stations' zoom they go at once, not when the GL map next settles.
        if (map.getZoom() < SUBWAY_MIN_ZOOM + 1 && stations.size) clearStations();
    };

    container.addEventListener('pointermove', onPointerMove);
    container.addEventListener('pointerleave', onPointerLeave);
    container.addEventListener('pointerdown', onPointerDown, true);
    container.addEventListener('click', onClick, true);
    container.addEventListener('dblclick', onDoubleClick, true);
    document.addEventListener('keydown', onKeyDown);
    map.on('movestart zoomstart', onMoveStart);
    map.on('moveend zoomend', onMoveEnd);

    return {
        add(glMap) {
            glMaps.add(glMap);
            glMap.on('idle', queueStations);
            queueStations();
        },
        remove(glMap) {
            glMaps.delete(glMap);
            glMap.off('idle', queueStations);
            if (!glMaps.size) {
                clearHover();
                closeCard();
                clearStations();
            } else {
                queueStations();
            }
        },
        isEmpty() { return glMaps.size === 0; },
        setLanguage(next) {
            if (swedish === next) return;
            swedish = next;
            renderCard();
        },
        destroy() {
            clearHover();
            closeCard();
            clearStations();
            glMaps.forEach((glMap) => glMap.off('idle', queueStations));
            container.removeEventListener('pointermove', onPointerMove);
            container.removeEventListener('pointerleave', onPointerLeave);
            container.removeEventListener('pointerdown', onPointerDown, true);
            container.removeEventListener('click', onClick, true);
            container.removeEventListener('dblclick', onDoubleClick, true);
            document.removeEventListener('keydown', onKeyDown);
            map.off('movestart zoomstart', onMoveStart);
            map.off('moveend zoomend', onMoveEnd);
            if (moveFrame) cancelAnimationFrame(moveFrame);
        },
    };
};

const controllers = new WeakMap();
const languages = new WeakMap();
const controllerFor = (map) => {
    let controller = controllers.get(map);
    if (!controller) {
        controller = createController(map);
        controller.setLanguage(languages.has(map) ? languages.get(map) : true);
        controllers.set(map, controller);
    }
    return controller;
};

// A MapLibre instance with the transit layers (openFreeMap.js): its stops hover and open the card. Returns the
// detach for when the layer goes.
export const attachTransitStops = (map, glMap) => {
    const controller = controllerFor(map);
    controller.add(glMap);
    return () => {
        controller.remove(glMap);
        if (controller.isEmpty() && controllers.get(map) === controller) {
            controller.destroy();
            controllers.delete(map);
        }
    };
};

export const setTransitStopsLanguage = (map, swedish) => {
    languages.set(map, Boolean(swedish));
    controllers.get(map)?.setLanguage(Boolean(swedish));
};
